import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { controllers, iconsColor } from '../../controllers';
import { addPublicURL } from '../../Functions/AddPublicURL';
import { getIconPath } from '../../Functions/GetIconPath';
import { getHotelFeatures } from '../../Functions/HotelFeatures';
import { addActuator, setPendingThermostatUsage, setSelectingDevice } from '../../redux/reducers/appReducer';
import { addDevice, resetSelectedDevicePosition } from '../../redux/reducers/controllersReducer';
import {
    setDefaultDevice,    
    setDeviceColor,
    setDeviceColors,
    setDeviceColorsShortcut,
    setDeviceName,
    setDeviceShape,
    setNumberOfButtons } from '../../redux/reducers/newDeviceReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { DeviceSelectorCategory } from '../../redux/types/app';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import DropDownList, { ListItemI } from '../DropDownList/DropDownList';

import styles from '../NewDevice/NewDevice.module.scss';

type IconSlot = {
    position: number;
    color: string;
    icon: string;
};

const actuatorUsageIcons: Record<string, string> = {
    switching: 'switching_actuator_box_blue',
    dimming: 'dimmers_actuator_box_blue',
    curtains: 'actuator_curtain_box_blue'
};

interface DeviceSelectorProps {
    type: DeviceSelectorCategory;
}

const DeviceSelector: React.FC<DeviceSelectorProps> = ({ type }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    
    const tech = useAppSelector((state) => state.app.controllerType) || false;
    const pendingThermostatUsage = useAppSelector((state) => state.app.pendingThermostatUsage);
    const [material, setMaterial] = useState(useAppSelector((state) => state.newDevice.material));
    const [numberOfButtons, setButtons] = useState(useAppSelector((state) => state.newDevice.icons));
    const [shape, setShape] = useState(useAppSelector((state) => state.newDevice.round));
    const [color, setColor] = useState(useAppSelector((state) => state.newDevice.colors[0]));
    const [previewImage, setPreviewImage] = useState(useAppSelector((state) => state.newDevice.previewImage));
    const [label, setLabel] = useState(useAppSelector((state) => state.newDevice.name) + useAppSelector((state) => state.newDevice.colorsShortcut[0]));
    const [enableSave, setEnableSave] = useState(true);
    const [includeHotelFeatures, setIncludeHotelFeatures] = useState(true);
    const position = { x: useAppSelector((state) => state.controllers.selectedDevice.x), y: useAppSelector((state) => state.controllers.selectedDevice.y) };
    const devices = useAppSelector((state) => state.controllers.devices);
    const actuators = useAppSelector((state) => state.app.actuators);
    const buttonOptions = type === 'infront_of_door_unit' ? [3] : [2, 4, 6, 7, 8, 9];
    const [hasActuator, setHasActuator] = useState(false);
    const controllerActuatorTypes = useMemo((): ListItemI[] => {
        const usages = Array.from(new Set(
            controllers
                .filter((controller) => controller.tech === tech && (controller.selectorCategory ?? controller.type) === type && controller.actuator)
                .map((controller) => controller.actuatorDetails?.usage)
        )).filter((usage): usage is string => usage !== undefined);

        return usages.map((usage) => ({
            id: usage,
            value: usage,
            content: t(`leftMenu.actuators.${usage}`),
            icon: getIconPath(actuatorUsageIcons[usage] ?? 'switching_actuator_box_blue')
        }));
        // eslint-disable-next-line
    }, [controllers, tech, type]);

    const thermostatUsageTypes = useMemo((): ListItemI[] => {
        const usages = Array.from(new Set(
           controllers
            .filter((controller) => controller.tech === tech && controller.type === 'thermostat')
            .map((controller) => controller.thermostatAppliance) 
        )).filter((usage): usage is string => usage !== undefined);
        return usages.map((usage) => ({
            id: usage,
            value: usage,
            content: t(`new_device.thermostat_usages.${usage}`),
            icon: getIconPath(`controllers/black/icon_${usage}`)
        }));
        // eslint-disable-next-line
    }, [controllers, tech, t]);

    const [selectedActuatorUsage, setSelectedActuatorUsage] = useState<string>('');
    const [selectedThermostatUsageType, setSelectedThermostatUsageType] = useState<string>(type === 'thermostat' ? pendingThermostatUsage || '' : '');

    useEffect(() => {
        // clear the one-shot pending usage from the left-menu drag once it has been applied
        if (type === 'thermostat' && pendingThermostatUsage) {
            dispatch(setPendingThermostatUsage(null));
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!thermostatUsageTypes.some((item) => item.value === selectedThermostatUsageType)) {
            setSelectedThermostatUsageType((thermostatUsageTypes[0]?.value as string) ?? '');
        }
    }, [selectedThermostatUsageType, thermostatUsageTypes]);

    useEffect(() => {
        if (!hasActuator) {
            return;
        }

        if (!controllerActuatorTypes.some((item) => item.value === selectedActuatorUsage)) {
            setSelectedActuatorUsage((controllerActuatorTypes[0]?.value as string) ?? '');
        }
    }, [hasActuator, controllerActuatorTypes, selectedActuatorUsage]);

    const selectedActuatorTypeItem = controllerActuatorTypes.find((item) => item.value === selectedActuatorUsage) ?? controllerActuatorTypes[0];
    const selectedThermostatUsageItem = thermostatUsageTypes.find((item) => item.value === selectedThermostatUsageType) ?? thermostatUsageTypes[0];

    const availableTypeDevices = useMemo(() => {
        return controllers.filter((controller) =>
            controller.tech === tech
            && (controller.selectorCategory ?? controller.type) === type
            && (type !== 'thermostat'
                || !selectedThermostatUsageType
                || controller.thermostatAppliance === selectedThermostatUsageType)
        );
    }, [tech, type, selectedThermostatUsageType]);

    const availableMaterials = useMemo(() => {
        return Array.from(new Set(availableTypeDevices.map((controller) => controller.material)));
    }, [availableTypeDevices]);

    const availableShapes = useMemo(() => {
        return Array.from(new Set(
            availableTypeDevices
                .filter((controller) => controller.material === material)
                .map((controller) => controller.round)
        ));
    }, [availableTypeDevices, material]);

    const availableButtonCounts = useMemo(() => {
        return Array.from(new Set(
            availableTypeDevices
                .filter((controller) => controller.material === material && controller.round === shape)
                .map((controller) => controller.icons)
        ));
    }, [availableTypeDevices, material, shape]);

    const hasActuatorOptionAvailable = useMemo(() => {
        return availableTypeDevices.some((controller) =>
            controller.material === material
            && controller.round === shape
            && controller.icons === numberOfButtons
            && controller.actuator === true
        );
    }, [availableTypeDevices, material, shape, numberOfButtons]);

    const hasBatteryOptionAvailable = availableTypeDevices.some((controller) =>
        controller.material === material
        && controller.round === shape
        && controller.icons === numberOfButtons
        && !controller.actuator
    );

    const isMaterialAvailable = useCallback((value: string) => {
        return availableMaterials.includes(value);
    }, [availableMaterials]);

    const isShapeAvailable = useCallback((value: boolean) => {
        return availableShapes.includes(value);
    }, [availableShapes]);

    const isButtonCountAvailable = useCallback((value: number) => {
        return availableButtonCounts.includes(value);
    }, [availableButtonCounts]);

    useEffect(() => {
        if(!tech) dispatch(setSelectingDevice(false)); 
    }, [tech, dispatch]);

    useEffect(() => {
        if (!availableTypeDevices.length) {
            return;
        }

        if (!availableMaterials.includes(material) && availableMaterials.length > 0) {
            setMaterial(availableMaterials[0]);
            return;
        }

        if (!availableShapes.includes(shape) && availableShapes.length > 0) {
            setShape(availableShapes[0]);
            dispatch(setDeviceShape(availableShapes[0]));
            return;
        }

        if (!availableButtonCounts.includes(numberOfButtons) && availableButtonCounts.length > 0) {
            setButtons(availableButtonCounts[0]);
            dispatch(setNumberOfButtons(availableButtonCounts[0]));
        }
    }, [
        availableTypeDevices,
        availableMaterials,
        availableShapes,
        availableButtonCounts,
        material,
        shape,
        numberOfButtons,
        dispatch
    ]);

    useEffect(() => {
        if (hasActuator && !hasActuatorOptionAvailable) {
            setHasActuator(false);
        } else if (!hasBatteryOptionAvailable && hasActuatorOptionAvailable) {
            setHasActuator(true);
        }
    }, [hasActuator, hasActuatorOptionAvailable, hasBatteryOptionAvailable]);

    const getDevice = useCallback(() => {
        return controllers.find((controller) =>
            (controller.selectorCategory ?? controller.type) === type
            && controller.material === material
            && controller.round === shape
            && controller.icons === numberOfButtons
            && controller.tech === tech
            && controller.actuator === hasActuator
            && (!hasActuator || !selectedActuatorUsage || controller.actuatorDetails?.usage === selectedActuatorUsage)
            && (type !== 'thermostat' || !selectedThermostatUsageType || controller.thermostatAppliance === selectedThermostatUsageType)
        );
    }, [type, material, shape, numberOfButtons, tech, hasActuator, selectedActuatorUsage, selectedThermostatUsageType]);
    const selectedDevice = getDevice();
    const changeMaterial = (material: string) => {
        if (!isMaterialAvailable(material)) {
            return;
        }
        setMaterial(material);
        if(material === 'metal'){
            dispatch(setDeviceShape(false));
            changeShape(false);
            changeColor('silver');
        }else{
            changeColor('black');
        }
    }
    const changeShape = (round: boolean) => {
        if (!isShapeAvailable(round)) {
            return;
        }
        dispatch(setDeviceShape(round));
        setShape(round);
    }
    const changeNumberOfButtons = (num: number) => {
        if (!isButtonCountAvailable(num)) {
            return;
        }
        dispatch(setNumberOfButtons(num));
        setButtons(num);
    }
    const changeColor = (color: string) => {
        dispatch(setDeviceColor(color));
        setColor(color);
        dispatch(setDeviceColors(getDevice()?.colors || []));
        dispatch(setDeviceColorsShortcut(getDevice()?.colorsShortcut || []));
        dispatch(setDeviceName(getDevice()?.name || ''));
    }
    
    useEffect(() => {
        const device = getDevice();
        if(!device){
            setLabel('');
            setEnableSave(false);
        }else{
            const previewImage = device.previewImage + (`-${color}`) + '.png';
            const pos = device.colors.findIndex((c) => c.toLowerCase() === color.toLowerCase());
            setPreviewImage(previewImage);
            setLabel(device.name + device.colorsShortcut[pos]);
            setEnableSave(true);
        }
    }, [material, shape, numberOfButtons, color, hasActuator, selectedActuatorUsage, selectedThermostatUsageType, t, getDevice]);

    const convertUnremoveableIconsData = (defaultIconsData: IconSlot[], color: string): IconSlot[] => {
        return defaultIconsData.map((slot) => ({
            ...slot,
            color: slot.color.replace('%c', color),
            icon: slot.icon.replace('%c', color)
        }));
    }

    const handleSubmit = () => {
        const controller = getDevice();
        if(!controller){
            toast.error(t('new_device.no_device'));
            return;
        }

        const maxExistingId = Math.max(
            0,
            ...devices.map((item) => item.id)
        );
        const nextId = maxExistingId + 1;

        const pos = controller.colors.findIndex((c) => c.toLowerCase() === color.toLowerCase());
        const hotelFeatures = getHotelFeatures(controller);
        const canRemoveHotelFeatures = 'removeableHotelFeatures' in controller && controller.removeableHotelFeatures === true;
        dispatch(setSelectingDevice(false));
        
        let iconsData;
        if(controller.unremoveableIcons){
            iconsData = convertUnremoveableIconsData(controller.defaultIconsData, iconsColor[color as keyof typeof iconsColor]);
        }else{
            iconsData = new Array(controller.icons).fill(0).map((_, index) => ({
                position: index,
                color: iconsColor[color as keyof typeof iconsColor],
                icon: 'add_icon'
            }));
        }

        dispatch(addDevice({
            hotelFeatures: canRemoveHotelFeatures && !includeHotelFeatures ? [] : hotelFeatures,
            id: nextId,
            name: controller.name,
            tech: controller.tech,
            type: controller.type,
            material: controller.material,
            icons: controller.icons,
            round: controller.round,
            color: color,
            colorShortcut: controller.colorsShortcut[pos],
            previewImage: controller.previewImage,
            className: controller.className,
            renderImage: controller.renderImage,
            state: 'error',
            price: controller.price,
            x: position.x ?? 50,
            y: position.y ?? 50,
            iconsData,
            thermostatAppliance: controller.thermostatAppliance,
            thermostatRequiredActuator: controller.thermostatRequiredActuator,
            thermostatRequiredActuatorMaxCount: controller.thermostatRequiredActuatorMaxCount
        }));

        if (controller.actuator && controller.actuatorDetails) {
            const maxActuatorId = Math.max(0, ...actuators.map((a) => a.id ?? 0));
            const nextActuatorId = maxActuatorId + 1;
            dispatch(addActuator({
                id: nextActuatorId,
                tech: controller.tech,
                type: controller.actuatorDetails.type,
                usage: controller.actuatorDetails.usage,
                applianceType: controller.actuatorDetails.applianceType,
                name: controller.actuatorDetails.name,
                refImg: controller.actuatorDetails.refImg,
                icon: controller.previewImage,
                dinrail: controller.actuatorDetails.dinrail,
                numberOfChannels: controller.actuatorDetails.numberOfChannels,
                maximumApplianceConnections: controller.actuatorDetails.numberOfChannels,
                builtIn: true,
                linkedControllerId: nextId,
                state: 'error'
            }));
        }
        dispatch(setDefaultDevice());
        dispatch(resetSelectedDevicePosition());
        toast.success(t('saved'));
    }
    return (
        <div id="tutorial-new-device" className={styles.NewDevice}>
            <div className={styles.NewDevice__wrapper}>
                <img src={getIconPath('arrow_back')} onClick={() => dispatch(setSelectingDevice(false))} className={styles.NewDevice__back} />
                <h2>{t('new_device.heading')}</h2>
                <div className={styles.NewDevice__controls}>
                    <div className={styles.NewDevice__controls__group}> 
                        <Button variant={material === 'glass' ? "fill" : "light"} size="lg" onClick={() => changeMaterial('glass')} disabled={!isMaterialAvailable('glass')}>{t('new_device.glass')}</Button>
                        <Button variant={material === 'metal' ? "fill" : "light"} size="lg" onClick={() => changeMaterial('metal')} disabled={!isMaterialAvailable('metal')}>{t('new_device.metal')}</Button>
                        <Button variant={material === 'plastic' ? "fill" : "light"} size="lg" onClick={() => changeMaterial('plastic')} disabled={!isMaterialAvailable('plastic')}>{t('new_device.plastic')}</Button>
                        {type === 'thermostat' && (
                            <div className={styles.NewDevice__controls__group__thermostat}>
                                <DropDownList
                                    items={thermostatUsageTypes}
                                    selectedItem={selectedThermostatUsageItem}
                                    onChange={(item) => setSelectedThermostatUsageType(item.value as string)}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.NewDevice__controls__group}>
                        <div className={styles.NewDevice__controls__group__item}>
                            <p>{t('new_device.color')}</p>
                            {(material === 'glass' || material === 'plastic') && (
                                <div className={styles.NewDevice__controls__group__item__input}>
                                    <span className={styles.NewDevice__controls__color} onClick={() => changeColor('black')} style={{ backgroundColor: '#000'}}></span>
                                    <span className={styles.NewDevice__controls__color} onClick={() => changeColor('white')} style={{ backgroundColor: '#fff'}}></span>
                                </div>
                            )}
                            {material === 'metal' && (
                                <div className={styles.NewDevice__controls__group__item__input}>
                                    <span className={styles.NewDevice__controls__color} onClick={() => changeColor('silver')} style={{ backgroundColor: '#A1A1A1'}}></span>
                                    <span className={styles.NewDevice__controls__color} onClick={() => changeColor('brass')} style={{ backgroundColor: '#D9C200'}}></span>
                                    <span className={styles.NewDevice__controls__color} onClick={() => changeColor('black')} style={{ backgroundColor: '#000'}}></span>
                                    <span className={styles.NewDevice__controls__color} onClick={() => changeColor('copper')} style={{ backgroundColor: '#834D00'}}></span>
                                </div>
                            )}
                        </div>
                        {material === 'glass' && (
                            <div className={styles.NewDevice__controls__group__item}>
                                <p>{t('new_device.shape')}</p>
                                <div className={styles.NewDevice__controls__group__item__input}>
                                    <span className={`${styles.NewDevice__controls__shape} ${!isShapeAvailable(false) ? styles.NewDevice__controls__shape__disabled : ''}`} onClick={() => changeShape(false)} style={!shape ? {border: '2px solid #0091ea'} : undefined}></span>
                                    <span className={`${styles.NewDevice__controls__shape} ${!isShapeAvailable(true) ? styles.NewDevice__controls__shape__disabled : ''}`} onClick={() => changeShape(true)} style={shape ? {border: '2px solid #0091ea', borderRadius: '.75rem'} : {borderRadius: '.75rem'}}></span>
                                </div>
                            </div>
                        )}
                        <div className={styles.NewDevice__controls__group__item}>
                            <p>{t('new_device.buttons')}</p>
                            <div className={styles.NewDevice__controls__group__item__input}>
                                {buttonOptions.map((buttonCount) => {
                                    const disabled = !isButtonCountAvailable(buttonCount);
                                    return (
                                        <span
                                            key={buttonCount}
                                            className={`${styles.NewDevice__controls__buttons} ${disabled ? styles.NewDevice__controls__buttons__disabled : ''}`}
                                            onClick={() => changeNumberOfButtons(buttonCount)}
                                            style={numberOfButtons === buttonCount ? {border: '2px solid #0091ea', fontWeight: 'bold'} : undefined}
                                        >
                                            {buttonCount}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    {((hasActuatorOptionAvailable && (material === 'glass' || material === 'plastic')) || (selectedDevice && 'removeableHotelFeatures' in selectedDevice && selectedDevice.removeableHotelFeatures === true)) && (
                        <div className={styles.NewDevice__controls__group}>
                            {hasActuatorOptionAvailable && (material === 'glass' || material === 'plastic') && (
                                <>
                                    <Checkbox label={t('new_device.battery')} checked={!hasActuator} disabled={!hasBatteryOptionAvailable} onChange={() => setHasActuator(false)} />
                                    <div className={styles.NewDevice__controls__group__checkboxitem}>
                                        <Checkbox label={t('new_device.with_actuator')} checked={hasActuator} onChange={() => setHasActuator(true)} />
                                        {hasActuator && (
                                            <DropDownList
                                                items={controllerActuatorTypes}
                                                selectedItem={selectedActuatorTypeItem}
                                                onChange={(item) => setSelectedActuatorUsage(item.value as string)}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                            {selectedDevice && 'removeableHotelFeatures' in selectedDevice && selectedDevice.removeableHotelFeatures === true && (
                                <Checkbox
                                    label={t('new_device.hotel_features')}
                                    checked={includeHotelFeatures}
                                    onChange={setIncludeHotelFeatures}
                                />
                            )}
                        </div>
                    )}
                    
                </div>
                <div className={styles.NewDevice__label}><p>{label || ''}</p></div>
                <div className={styles.NewDevice__preview}>
                    {enableSave && <img src={addPublicURL(`/assets/controllers/preview/${previewImage}`)} alt="Panel preview"/>}
                    {!enableSave && <div className={styles.NewDevice__preview__empty}>{t('new_device.no_device')}</div>}
                    {hasActuator && <img src={addPublicURL(`/assets/actuators/${getDevice()?.actuatorDetails?.refImg}`)} alt="Actuator preview"/>}
                </div>
                <div className={styles.NewDevice__save}>
                    {getDevice()?.datasheetUrl &&(
                        <a href={getDevice()?.datasheetUrl} target="_blank" rel="noopener noreferrer">{t('new_device.datasheet')}</a>
                    )}
                    <Button onClick={() => handleSubmit()} variant="fill" size="lg" disabled={!enableSave}>{t('apply')}</Button>
                </div>
            </div>
        </div>
    )
}

export default DeviceSelector;